<template>
  <b-col cols="12" xl="12">
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p class="mt-3">Loading profit report...</p>
    </div>

    <!-- Main Content - Only show when data is loaded -->
    <b-card v-else id="invoice" class="card border-0 hp-invoice-card">
      <!-- Watermark -->
      <img
        class="watermark"
        src="@/assets/img/pages/authentication/Wosslogo1.png"
        alt="Watermark"
      />

      <b-row align-h="between">
        <!-- Logo -->
        <b-col cols="12" lg="10">
          <img
            class="hp-logo mb-16"
            src="@/assets/img/pages/authentication/Wosslogo1.png"
            alt="WOSS Logo"
            style="width: 180px; height: auto;"
          />
        </b-col>

        <!-- Download Button -->
        <b-col lg="2" class="space-x-0">
          <b-button
            @click="downloadPdf"
            variant="primary"
            class="mb-8 mr-8 exclude-from-pdf"
          >
            Download
          </b-button>
        </b-col>

        <!-- Title -->
        <b-col
          cols="12"
          lg="12"
          class="d-flex justify-content-center align-items-center"
        >
          <h2
            class="hp-p1-body mb-16 text-center"
            style="color: black; font-weight: bold; font-size: 28px;"
          >
            PROFIT DISTRIBUTION REPORT
          </h2>
        </b-col>
      </b-row>

      <b-row align-h="between">
        <!-- Personal Data -->
        <div>
          <b-col cols="12" md="12" class="pb-16 hp-print-info">
            <table border="1" cellpadding="5" cellspacing="0" style="width: 100%; border-color: black; table-layout: fixed;">
            <thead>
                <tr>
                <th colspan="4" class="text-center" style="color: black; font-size: 18px; background-color: gray;">
                    PERSONAL DATA
                </th>
                </tr>
                <tr>
                <th class="bg-gray-100 text-center py-2 border-r border-gray-400">NAME</th>
                <th class="bg-gray-100 text-center py-2 border-r border-gray-400">PHONE</th>
                <th class="bg-gray-100 text-center py-2 border-r border-gray-400">SHARE (%)</th>
                <th class="bg-gray-100 text-center py-2 border-r border-gray-400">Current Company Asset Value</th>
                </tr>
            </thead>

            <tbody>
                <tr>
                <td style="text-align:center; font-weight:bold">{{ stakeholder?.name }}</td>
                <td style="text-align:center; font-weight:bold">{{ stakeholder?.phone }}</td>
                <td style="text-align:center; font-weight:bold">{{ stakeholder?.share_percentage }}%</td>
                <td style="text-align:center; font-weight:bold">{{ formatCurrency(profit?.asset) }}</td>
                </tr>

                <!-- Address & Email row -->
                <tr>
                <th class="bg-gray-100 text-center">EMAIL</th>
                <th class="bg-gray-100 text-center border-r border-gray-400" colspan="3">ADDRESS</th>
                </tr>
                <tr>
                <td style="text-align:center; font-weight:bold">{{ stakeholder?.email }}</td>
                <td colspan="3" style="text-align:center; font-weight:bold">{{ stakeholder?.address }}</td>
                </tr>
            </tbody>
            </table>
          </b-col>
        </div>

        <!-- Profit Table -->
        <div>
          <b-col cols="12" md="12" class="pb-16 hp-print-info">
            <table
              border="1"
              cellpadding="5"
              cellspacing="0"
              style="width: 100%; border-color: black; table-layout: fixed;"
            >
              <thead>
                <tr>
                  <th
                    colspan="6"
                    class="text-center"
                    style="color: black; font-size: 18px; background-color: gray;"
                  >
                    PROFIT DETAILS
                  </th>
                </tr>
                <tr>
                  <th class="text-center">Month-Year</th>
                  <th class="text-center">Total Earnings</th>
                  <th class="text-center">Total Expenses</th>
                  <th class="text-center">Net Profit</th>
                  <th class="text-center">Share (%)</th>
                  <th class="text-center">Stakeholder Profit</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="text-align:center; font-weight:bold">{{ formatMonth(profit?.month_year) }}</td>
                  <td style="text-align:center; font-weight:bold">{{ formatCurrency(profit?.total_earnings) }}</td>
                  <td style="text-align:center; font-weight:bold">{{ formatCurrency(profit?.total_expenses) }}</td>
                  <td style="text-align:center; font-weight:bold">{{ formatCurrency(profit?.total_profit) }}</td>
                  <td style="text-align:center; font-weight:bold">{{ stakeholder?.share_percentage }}%</td>
                  <td style="text-align:center; font-weight:bold">{{ formatCurrency(profit?.stakeholder_profit) }}</td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </div>

        <!-- Profit Certification -->
        <b-col cols="12" md="12" class="pb-16 hp-print-info">
        <table border="1" cellpadding="5" cellspacing="0" style="width: 100%; border-color: black;">
            <thead>
            <tr>
                <th colspan="2" class="text-center" style="color: black; font-size: 18px; background-color: gray;">
                PROFIT CERTIFICATION
                </th>
            </tr>
            </thead>
        </table>

        <div style="padding: 12px;">
            <p style="color: black; font-size: 13px; line-height: 1.6;">
            This document certifies the <b>profit distribution record</b> stored in the
            <b>WOSS AUTO-ID system</b>. It has been finalized in compliance with internal
            accounting standards and stakeholder agreements.
            </p>

            <p style="color: black; font-size: 13px; line-height: 1.6;">
            The profit details shown are for the finalized month of
            <b>{{ formatMonth(profit?.month_year) }}</b>.  
            According to the records:
            </p>

            <ul style="color: black; font-size: 13px; line-height: 1.8; margin-left: 20px;">
            <li>Total Earnings: <b>{{ formatCurrency(profit?.total_earnings) }}</b></li>
            <li>Total Expenses: <b>{{ formatCurrency(profit?.total_expenses) }}</b></li>
            <li>Net Profit: <b>{{ formatCurrency(profit?.total_profit) }}</b></li>
            <li>Stakeholder Share ({{ stakeholder?.share_percentage }}%): 
                <b>{{ formatCurrency(profit?.stakeholder_profit) }}</b>
            </li>
            </ul>

            <p style="color: black; font-size: 13px; line-height: 1.6;">
            This record serves as an official certification of the profit allocation
            for the month specified above. All data is secured and verified within the
            WOSS AUTO-ID system.
            </p>
        </div>
        </b-col>
      </b-row>

      <!-- Signature Section -->
      <b-row style="margin-top: 40px;">
        <b-col cols="12">
          <b-table-simple small caption-top responsive borderless>
            <b-thead>
              <b-tr>
                <b-th>
                  <div style="width: 250px; text-align: center;">
                    <img
                      src="@/assets/img/pages/authentication/car-insurance-icon.jpg"
                      alt="Seller"
                      class="signature-watermark-icon"
                    />
                    <p style="border-bottom: 1px solid black;"></p>
                    <p style="font-size: 8px;">Name and Signature from Seller</p>
                  </div>
                </b-th>
                <b-th>
                  <div style="width: 250px; text-align: center;">
                    <img
                      src="@/assets/img/pages/authentication/car-insurance-icon.jpg"
                      alt="Installer"
                      class="signature-watermark-icon"
                    />
                    <p style="border-bottom: 1px solid black;"></p>
                    <p style="font-size: 8px;">Name and Signature from Installer</p>
                  </div>
                </b-th>
                <b-th>
                  <div style="width: 250px; text-align: center;">
                    <img
                      src="@/assets/img/pages/authentication/original-quality-rubber-stamp.jpg"
                      alt="Owner"
                      class="signature-watermark-icon"
                    />
                    <p style="border-bottom: 1px solid black;"></p>
                    <p style="font-size: 8px;">
                      Name and Signature or Stamp of Owner/Representative
                    </p>
                  </div>
                </b-th>
              </b-tr>
            </b-thead>
          </b-table-simple>
        </b-col>
      </b-row>
    </b-card>

    <!-- Error State (Optional) -->
    <b-alert v-if="error" show variant="danger" class="mt-3">
      <h4>Error Loading Report</h4>
      <p>{{ error }}</p>
      <b-button @click="fetchProfit" variant="outline-danger">Try Again</b-button>
    </b-alert>
  </b-col>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BCard,
  BSpinner,
  BAlert,
} from "bootstrap-vue";
import axios from "../../../axios";
import html2pdf from "html2pdf.js";

export default {
  data() {
    return {
      stakeholder: null,
      profit: null,
      email: "",
      street_address: "",
      isLoading: true,
      error: null,
    };
  },

  created() {
    this.fetchProfit();
  },

  methods: {
    async fetchProfit() {
      try {
        this.isLoading = true;
        this.error = null;
        
        const profitId = this.$route.params.id;
        const res = await axios.get(`/stakeholders/profits/${profitId}`);
        
        this.profit = res.data.data;
        this.stakeholder = res.data.data.stakeholder;
        this.email = this.stakeholder?.email || "";
        this.street_address = this.stakeholder?.street_address || "";
        
        this.isLoading = false;
      } catch (err) {
        console.error("Error fetching profit report:", err);
        this.error = "Failed to load profit report. Please try again.";
        this.isLoading = false;
      }
    },

    formatMonth(value) {
      if (!value) return "";
      const d = new Date(value + "-01");
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value || 0);
    },

    downloadPdf() {
      const invoiceElement = document.getElementById("invoice");
      const excludeElements = document.querySelectorAll(".exclude-from-pdf");
      excludeElements.forEach((el) => (el.style.display = "none"));

      const options = {
        filename: `profit_report_${this.stakeholder?.name}_${this.profit?.month_year}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      html2pdf()
        .from(invoiceElement)
        .set(options)
        .save()
        .finally(() => {
          excludeElements.forEach((el) => (el.style.display = ""));
        });
    },
  },

  components: {
    BRow,
    BCol,
    BButton,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BCard,
    BSpinner,
    BAlert,
  },
};
</script>

<style scoped>
#invoice {
  position: relative;
}

.watermark {
  position: absolute;
  top: 40%;   /* Moved further down */
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.30;
  z-index: 0;
  width: 600px;
  height: auto;
  pointer-events: none;
}

.signature-watermark-icon {
  width: 100px;
  height: auto;
  margin-bottom: 4px;
}
</style>